// src/features/organisation/components/hierarchy/DelegationDetailForm.tsx
import React, { useMemo } from 'react';
import { Share2 } from 'lucide-react';
import { apiClient } from '../../../../services/api/client';
import { useEmployees } from '../../hooks/useEmployees';
import { useSites } from '../../hooks/useSites';
import {
  EntityFormLayout,
  EntityFormHeader,
  EntityFormFooter,
  EditableField
} from '../../../../components/common/forms';
import { useEntityForm, FieldConfig } from '../../../../components/common/hooks';

interface SiteDelegation {
  id?: number;
  tenantId?: string;
  managerId: number;
  supervisedSiteId: number;
  delegationType: string;
  effectiveFrom: string;
  effectiveTo?: string;
  isActive?: boolean;
}

const DELEGATION_TYPES = [
  { value: 'FULL', label: 'Complète' },
  { value: 'QHSE_ONLY', label: 'QHSE uniquement' },
  { value: 'DEPARTMENTAL', label: 'Départementale' },
  { value: 'TEMPORARY', label: 'Temporaire' },
];

interface DelegationDetailFormProps {
  delegationId: number | null;
  onClose: () => void;
  onSave?: () => void;
}

const DelegationDetailForm: React.FC<DelegationDetailFormProps> = ({
  delegationId,
  onClose,
  onSave: onSaveCallback,
}) => {
  const { employees } = useEmployees();
  const { sites } = useSites();
  const [loading, setLoading] = React.useState(!!delegationId);
  const [delegation, setDelegation] = React.useState<SiteDelegation | null>(null);

  React.useEffect(() => {
    if (delegationId) {
      setLoading(true);
      apiClient.get(`/organisation/site-delegations/${delegationId}`)
        .then((data) => setDelegation(data))
        .finally(() => setLoading(false));
    } else {
      setDelegation(null);
    }
  }, [delegationId]);

  const fields: FieldConfig[] = useMemo(() => [
    {
      name: 'managerId',
      label: 'Manager',
      type: 'select',
      required: true,
      options: (employees || []).map((e: { id: number; firstName: string; lastName: string; employeeCode: string; employeeStatus?: { code: string } }) => ({
        value: e.id,
        label: `${e.firstName} ${e.lastName}`,
      })),
      render: (value) => {
        const emp = (employees || []).find((e: { id: number; firstName: string; lastName: string; employeeCode: string; employeeStatus?: { code: string } }) => e.id === value);
        return emp ? `${emp.firstName} ${emp.lastName}` : 'Non défini';
      },
    },
    {
      name: 'supervisedSiteId',
      label: 'Site supervisé',
      type: 'select',
      required: true,
      options: (sites || []).map((s: { id: number; siteName: string }) => ({ value: s.id, label: s.siteName })),
      render: (value) => (sites || []).find((s: { id: number; siteName: string }) => s.id === value)?.siteName || 'Non défini',
    },
    {
      name: 'delegationType',
      label: 'Type de délégation',
      type: 'select',
      required: true,
      options: DELEGATION_TYPES,
      render: (value) => DELEGATION_TYPES.find(t => t.value === value)?.label || value,
    },
    {
      name: 'effectiveFrom',
      label: "Date d'effet",
      type: 'date',
      required: true,
    },
    {
      name: 'effectiveTo',
      label: 'Date de fin',
      type: 'date',
    },
  ], [employees, sites]);

  const transformToFormData = (d: SiteDelegation | null) => {
    if (!d) return {
      managerId: 0,
      supervisedSiteId: 0,
      delegationType: 'FULL',
      effectiveFrom: new Date().toISOString().split('T')[0],
      effectiveTo: '',
    };
    return {
      managerId: d.managerId,
      supervisedSiteId: d.supervisedSiteId,
      delegationType: d.delegationType,
      effectiveFrom: d.effectiveFrom ? d.effectiveFrom.split('T')[0] : '',
      effectiveTo: d.effectiveTo ? d.effectiveTo.split('T')[0] : '',
    };
  };

  const handleSave = async (formData: Record<string, unknown>) => {
    const data = {
      managerId: formData.managerId,
      supervisedSiteId: formData.supervisedSiteId,
      delegationType: formData.delegationType,
      effectiveFrom: formData.effectiveFrom,
      effectiveTo: formData.effectiveTo || undefined,
    };
    if (delegationId) {
      await apiClient.put(`/organisation/site-delegations/${delegationId}`, data);
    } else {
      await apiClient.post('/organisation/site-delegations', data);
    }
    onSaveCallback?.();
    onClose();
    return data;
  };

  const {
    formData,
    hasChanges,
    saving,
    errors,
    handleFieldChange,
    startEditing,
    stopEditing,
    isEditing,
    handleSave: onFormSave,
  } = useEntityForm({
    entity: delegation,
    fields,
    onSave: handleSave,
    transformToFormData,
  });

  const managerLabel = delegation
    ? (() => {
        const emp = (employees || []).find((e: { id: number; firstName: string; lastName: string; employeeCode: string; employeeStatus?: { code: string } }) => e.id === delegation.managerId);
        return emp ? `${emp.firstName} ${emp.lastName}` : undefined;
      })()
    : undefined;

  return (
    <EntityFormLayout loading={loading} maxWidth="2xl">
      <EntityFormHeader
        title={delegation ? `Délégation — ${managerLabel || ''}` : 'Nouvelle délégation'}
        icon={Share2}
        hasChanges={hasChanges}
        saving={saving}
        onSave={onFormSave}
        onClose={onClose}
      />

      <div className="flex-1 overflow-y-auto p-6">
        {formData && (
          <div className="grid grid-cols-2 gap-4">
            {fields.map(field => (
              <div key={field.name} className={field.colSpan === 2 ? 'col-span-2' : ''}>
                <EditableField
                  field={field}
                  value={formData[field.name]}
                  isEditing={isEditing(field.name)}
                  onStartEdit={() => startEditing(field.name)}
                  onStopEdit={() => stopEditing(field.name)}
                  onChange={(value) => handleFieldChange(field.name, value)}
                  error={errors[field.name]}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <EntityFormFooter helpText="💡 Double-cliquez sur un champ pour le modifier." />
    </EntityFormLayout>
  );
};

export default DelegationDetailForm;
